import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Clock, CheckCircle2, XCircle, X, PencilLine } from "lucide-react";
import { requestTakedown, getReleaseRequest } from "@/lib/afmApi";

const REASONS = [
  ["duplicate", "Lanzamiento duplicado"],
  ["metadata_error", "Error en los datos o la portada"],
  ["no_longer", "Ya no quiero publicarlo"],
  ["rights", "Problema de derechos"],
  ["other", "Otro motivo"],
];

const KIND = { takedown: "retiro", correction: "corrección" };
const ADM_STATUS = { pending: "Enviada a la distribuidora", in_review: "En revisión por la distribuidora", accepted: "Aceptada por la distribuidora, en proceso", rejected: "No aceptada por la distribuidora", completed: "Completada en las tiendas" };

const errText = (e) => e?.response?.data?.detail || "No se pudo enviar la solicitud";

// Botón "Solicitar retiro" para el artista + estado de su solicitud.
export default function TakedownRequest({ releaseId, releaseStatus }) {
  const [req, setReq] = useState(undefined);
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("takedown");
  const [reason, setReason] = useState("duplicate");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { getReleaseRequest(releaseId).then(setReq).catch(() => setReq(null)); }, [releaseId]);

  const send = async () => {
    if (kind === "correction" && message.trim().length < 10) { toast.error("Explica qué hay que corregir (mínimo 10 caracteres)"); return; }
    if (reason === "other" && !message.trim()) { toast.error("Cuéntanos el motivo"); return; }
    setSaving(true);
    try {
      const r = await requestTakedown({ release_id: releaseId, kind, reason: kind === "correction" ? "metadata_error" : reason, message: message.trim() });
      setReq(r); setOpen(false);
      toast.success("Solicitud enviada. El equipo de Always Fresh la revisará.");
    } catch (e) { toast.error(errText(e)); } finally { setSaving(false); }
  };

  if (req === undefined || releaseStatus === "takedown") return null;

  if (req && req.state === "pending") {
    return (
      <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="takedown-pending">
        <Clock className="mt-0.5 h-4 w-4 shrink-0" />
        <div><strong>Solicitud de {KIND[req.kind] || "retiro"} enviada.</strong> El equipo de Always Fresh está revisando tu solicitud.</div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {req && req.state === "rejected" && (
        <div className="flex items-start gap-2 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700" data-testid="takedown-rejected">
          <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
          <div><strong>Tu última solicitud de {KIND[req.kind] || "retiro"} no fue aprobada.</strong>{req.admin_note ? ` ${req.admin_note}` : ""}</div>
        </div>
      )}
      {req && req.state === "approved" && (
        <div className="flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> <div><strong>{req.kind === "correction" ? "Corrección aprobada por Always Fresh." : "Retiro aprobado por Always Fresh."}</strong>{req.adamantem_request?.status !== "completed" ? " El cambio en tiendas se confirma cuando la distribuidora lo complete." : ""}{req.adamantem_request?.status && ADM_STATUS[req.adamantem_request.status] ? ` ${ADM_STATUS[req.adamantem_request.status]}.` : ""}</div>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => { setKind("correction"); setOpen(true); }} data-testid="request-correction" className="flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:border-black">
          <PencilLine className="h-3.5 w-3.5" /> Solicitar corrección
        </button>
        <button onClick={() => { setKind("takedown"); setOpen(true); }} data-testid="request-takedown" className="flex items-center gap-2 rounded-lg border border-red-200 px-4 py-2 text-xs font-semibold text-red-700 hover:border-red-400 hover:bg-red-50">
          <Trash2 className="h-3.5 w-3.5" /> Solicitar retiro
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={() => setOpen(false)}>
          <div className="my-16 w-full max-w-md rounded-2xl border border-slate-300 bg-white shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="takedown-dialog">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <h2 className="font-serif text-lg font-bold text-black">{kind === "correction" ? "Solicitar corrección" : "Solicitar retiro"}</h2>
              <button onClick={() => setOpen(false)}><X className="h-5 w-5 text-slate-400 hover:text-black" /></button>
            </div>
            <div className="space-y-4 p-6">
              <p className="text-sm text-slate-600">{kind === "correction" ? "Dinos qué dato hay que corregir (título, nombre, créditos, portada…). Always Fresh lo revisa y, si ya está en tiendas, se lo pide a la distribuidora." : "El equipo de Always Fresh revisará tu solicitud. Si el tema ya está en tiendas, se pedirá que lo bajen; puede tardar unos días."}</p>
              {kind === "takedown" && <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Motivo</label>
                <select value={reason} onChange={(e) => setReason(e.target.value)} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-black">
                  {REASONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>}
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">{kind === "correction" ? "Qué hay que corregir *" : `Detalle ${reason === "other" ? "*" : "(opcional)"}`}</label>
                <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} maxLength={1000} className="w-full resize-none rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-black" placeholder="Cuéntanos qué pasó…" />
              </div>
            </div>
            <div className="flex justify-end gap-3 border-t border-slate-200 px-6 py-4">
              <button onClick={() => setOpen(false)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium hover:border-black">Cancelar</button>
              <button onClick={send} disabled={saving} data-testid="takedown-submit" className="rounded-lg bg-red-700 px-5 py-2 text-sm font-semibold text-white disabled:opacity-50">{saving ? "Enviando…" : "Enviar solicitud"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
